import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, User } from 'lucide-react';
import FavoriteButton from './FavoriteButton';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgZmlsbD0iIzI3MjcyYSIvPjx0ZXh0IHg9IjIwMCIgeT0iMjUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNTI1MjVhIiBmb250LXNpemU9IjQ4Ij7wn5GMPC90ZXh0Pjwvc3ZnPg==';

const FreelancerCard = ({ freelancer, showFavorite = true, index = 0 }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const imageUrl = freelancer.images?.[0] || freelancer.image || PLACEHOLDER_IMAGE;
  const staggerClass = index < 12 ? `stagger-${index + 1}` : '';

  return (
    <Link to={`/escort/${freelancer.slug || freelancer.id}`} className={`group block animate-card-enter ${staggerClass}`}>
      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-zinc-900 shadow-lg shadow-black/30 group-hover:shadow-xl group-hover:shadow-emerald-500/10 transition-all duration-300 group-hover:-translate-y-2 active:scale-[0.98]">
        {/* Skeleton while loading */}
        {!imgLoaded && !imgError && (
          <div className="absolute inset-0 skeleton" />
        )}

        {/* Image */}
        <img
          src={imgError ? PLACEHOLDER_IMAGE : imageUrl}
          alt={freelancer.name}
          loading="lazy"
          className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ease-out ${imgLoaded ? 'opacity-100' : 'opacity-0 scale-105'}`}
          onLoad={() => setImgLoaded(true)}
          onError={() => { setImgError(true); setImgLoaded(true); }}
        />

        {/* Favorite Button - always visible on mobile */}
        {showFavorite && (
          <div className="absolute top-2.5 right-2.5 z-10 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 sm:translate-y-1 sm:group-hover:translate-y-0">
            <FavoriteButton listingId={freelancer.id} size="small" />
          </div>
        )}

        {/* Gradient Overlay - enhanced */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 transform transition-transform duration-300 group-hover:translate-y-0">
          <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-1 group-hover:text-emerald-400 transition-colors duration-200 truncate">
            {freelancer.name}
          </h3>
          {freelancer.city && (
            <div className="flex items-center gap-1.5 text-zinc-400 text-xs sm:text-sm">
              <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 text-emerald-500/70" />
              <span className="truncate">{freelancer.city}</span>
            </div>
          )}
        </div>

        {/* No image placeholder icon */}
        {imgError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <User className="w-16 h-16 text-zinc-700" />
          </div>
        )}

        {/* Hover border glow */}
        <div className="absolute inset-0 rounded-xl ring-1 ring-white/5 group-hover:ring-emerald-500/20 transition-all duration-300" />
      </div>
    </Link>
  );
};

// Skeleton card for loading states
export const FreelancerCardSkeleton = () => (
  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-zinc-900 relative">
    <div className="w-full h-full skeleton" />
    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
      <div className="h-5 w-2/3 skeleton rounded" />
      <div className="h-3 w-1/3 skeleton rounded" />
    </div>
  </div>
);

export default FreelancerCard;
